import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Film, Flag, Store, Trash2, Clock, Crown, Heart } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import ProgressBar from "../components/ProgressBar";

function timeLeft(endDate) {
  const ms = new Date(endDate).getTime() - Date.now();
  if (ms <= 0) return "Ended — waiting to finalize";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (d > 0) return `${d}d ${h}h ${m}m left`;
  if (h > 0) return `${h}h ${m}m left`;
  return `${m}m left`;
}

export default function ReelDetail() {
  const { id } = useParams();
  const [reel, setReel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const r = await api.getReel(id);
      setReel(r);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reel not found");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [id]); // eslint-disable-line
  useEffect(() => {
    if (!reel || reel.status !== "active") return;
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [reel?.status]);

  if (loading) return <div className="p-12 text-center text-sm text-gray-500">Loading reel…</div>;
  if (!reel) return <div className="p-12 text-center text-sm text-gray-500">Reel not found</div>;

  const onFinalize = async () => {
    if (!window.confirm(`Finalize "${reel.name}" now? Top ${reel.winnersCount} winner(s) flip to pre-order.`)) return;
    setBusy(true);
    try {
      const r = await api.finalizeReel(id);
      toast.success(`Finalized — ${r.winners.length} winner(s) advanced`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not finalize");
    } finally {
      setBusy(false);
    }
  };

  const onDelete = async () => {
    if (!window.confirm(`Delete "${reel.name}" and ALL its campaigns, votes, pre-orders?`)) return;
    setBusy(true);
    try {
      await api.deleteReel(id);
      toast.success("Reel deleted");
      navigate("/app/reels");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not delete");
      setBusy(false);
    }
  };

  const campaigns = reel.campaigns || [];
  const totalVotes = campaigns.reduce((s, c) => s + (c.currentVoteCount || 0), 0);

  return (
    <div className="space-y-6" data-testid="reel-detail-page">
      <div>
        <Link to="/app/reels" className="inline-flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft size={14} /> Back to reels
        </Link>
        <div className="flex items-end justify-between gap-4 flex-wrap mt-3">
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight" data-testid="reel-title">{reel.name}</h1>
              {reel.status === "active" ? (
                <span className="lp-badge lp-badge-voting">Active</span>
              ) : (
                <span className="lp-badge lp-badge-closed">Completed</span>
              )}
            </div>
            {reel.description && <p className="text-sm text-gray-600 mt-2 max-w-2xl">{reel.description}</p>}
          </div>
          <div className="flex items-center gap-2">
            <Link to={`/storefront/reels/${reel.id}`} className="lp-btn-secondary" data-testid="reel-storefront-btn">
              <Store size={16} /> View on storefront
            </Link>
            {reel.status === "active" && (
              <button onClick={onFinalize} disabled={busy} className="lp-btn-primary" data-testid="finalize-reel-btn">
                <Flag size={16} /> {busy ? "…" : "Finalize now"}
              </button>
            )}
            <button
              onClick={onDelete}
              disabled={busy}
              className="lp-btn-secondary text-red-600 hover:bg-red-50 hover:border-red-200"
              data-testid="delete-reel-btn"
              title="Delete reel"
            >
              <Trash2 size={16} />
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Competing videos" value={campaigns.length} icon={Film} />
        <Stat label="Winners" value={`Top ${reel.winnersCount}`} icon={Crown} />
        <Stat label="Total votes" value={totalVotes} icon={Heart} />
        <Stat
          label={reel.status === "active" ? "Time left" : "Ended"}
          value={reel.status === "active" ? timeLeft(reel.endDate) : new Date(reel.endDate).toLocaleDateString()}
          icon={Clock}
        />
      </div>

      <div className="lp-card overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-medium tracking-tight">Live leaderboard</h2>
          <p className="text-xs text-gray-500 mt-1">
            {reel.status === "active"
              ? `Top ${reel.winnersCount} highlighted — they advance at the deadline.`
              : "Final results."}
          </p>
        </div>
        <div className="p-4 space-y-3" data-testid="reel-admin-leaderboard">
          {campaigns.map((c, i) => {
            const isWinner = i < reel.winnersCount;
            const pct = totalVotes > 0 ? Math.round((c.currentVoteCount / totalVotes) * 100) : 0;
            return (
              <div
                key={c.id}
                data-testid={`reel-admin-row-${i}`}
                className={`flex items-center gap-4 p-4 rounded-xl border ${
                  isWinner ? "border-amber-200 bg-amber-50/50" : "border-gray-200 bg-white"
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold shrink-0 ${
                  i === 0 ? "bg-amber-500 text-white" :
                  isWinner ? "bg-amber-200 text-amber-900" : "bg-gray-100 text-gray-700"
                }`}>
                  {i + 1}
                </div>
                {c.productImageUrl && (
                  <img src={c.productImageUrl} alt="" className="w-12 h-12 rounded-lg object-cover border border-gray-100 shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-gray-900 truncate">{c.productTitle}</div>
                    {c.status === "pre_order" && <span className="lp-badge lp-badge-preorder text-[10px]">Pre-order</span>}
                    {c.status === "closed" && <span className="lp-badge lp-badge-closed text-[10px]">Closed</span>}
                    {(c.videoUrl || c.productVideoUrl) && (
                      <span className="text-[10px] text-emerald-700 inline-flex items-center gap-1">
                        <Film size={10} />
                      </span>
                    )}
                  </div>
                  <div className="mt-1.5">
                    <ProgressBar value={c.currentVoteCount} max={Math.max(1, totalVotes)} testId={`reel-admin-bar-${i}`} />
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xl font-semibold tabular-nums">{c.currentVoteCount}</div>
                  <div className="text-xs text-gray-500">{pct}%</div>
                </div>
              </div>
            );
          })}
          {campaigns.length === 0 && (
            <div className="p-8 text-center text-sm text-gray-500">No videos in this reel.</div>
          )}
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="lp-card p-5">
    <div className="flex items-start justify-between">
      <div>
        <div className="lp-section-label">{label}</div>
        <div className="text-2xl font-semibold tracking-tight mt-2">{value}</div>
      </div>
      <div className="w-9 h-9 rounded-xl bg-gray-50 border border-gray-100 flex items-center justify-center">
        <Icon size={16} className="text-gray-700" />
      </div>
    </div>
  </div>
);
